/**
 * Computes CET via Newton-Raphson IRR solver.
 * @see specs/domain/regras-negocio.md
 */
export function calcCET({ valor, parcelas, taxaMensal, iof: iofIn, tarifas = 0 }) {
    const iof = iofIn ?? calcIOF(valor, parcelas, taxaMensal);
    const valorLiquido = valor - iof - tarifas;
    if (valorLiquido <= 0)
        throw new Error("valor_liquido_invalido");
    const parcela = calcPMT(valor, parcelas, taxaMensal);
    let i = taxaMensal * 1.1;
    for (let iter = 0; iter < 100; iter++) {
        let f = -valorLiquido;
        let df = 0;
        for (let k = 1; k <= parcelas; k++) {
            const den = Math.pow(1 + i, k);
            f += parcela / den;
            df -= (k * parcela) / (den * (1 + i));
        }
        const next = i - f / df;
        if (Math.abs(next - i) < 1e-9) {
            i = next;
            break;
        }
        i = next;
    }
    return {
        mensal: i,
        anual: Math.pow(1 + i, 12) - 1,
        parcela,
        valorLiquido,
        iof,
        totalPago: parcela * parcelas,
    };
}
/** IOF adicional (aliquota fixa sobre o principal da operacao). */
export const IOF_ADICIONAL = 0.0038;
/** IOF diario para pessoa fisica. */
export const IOF_DIARIO = 0.000082;
/** Teto legal de dias do IOF diario — POR PARCELA, nao pela operacao. */
export const IOF_DIAS_MAX = 365;
/**
 * IOF de credito parcelado a pessoa fisica.
 *
 * O IOF diario incide sobre o PRINCIPAL DE CADA PARCELA, pelos dias entre a
 * liberacao e o vencimento dela (teto de 365 dias por parcela) — nao sobre o
 * valor cheio pelo prazo inteiro. Num emprestimo que amortiza todo mes o saldo
 * cai a cada parcela, e cobrar como se o total ficasse em aberto ate o fim
 * infla o imposto.
 *
 * Era exatamente o que a formula anterior fazia (`valor * 0.000082 *
 * parcelas*30`), e o erro nao era pequeno: R$ 2.400 em 8x davam R$ 56,35 em vez
 * de R$ 36,17 (+56%); R$ 5.000 em 12x, R$ 166,60 em vez de R$ 101,56 (+64%).
 * Some nos prazos longos (+5% em 60x), onde quase toda parcela ja bate o teto
 * de 365 dias. Como o IOF e' descontado do valor liberado, o servidor recebia
 * menos do que devia — e o CET, calculado sobre esse liquido, saia mais alto
 * que o real. Cliente 07/08/2026.
 *
 * Convencao de 30 dias por mes, a mesma do resto do sistema.
 *
 * ATENCAO: as aliquotas acima sao parametro fiscal, nao regra de negocio nossa.
 * Se a legislacao mudar, mexer nas constantes — a metodologia continua valendo.
 */
export function calcIOF(valor, parcelas, taxaMensal) {
    if (!(valor > 0) || !(parcelas > 0))
        return 0;
    const i = taxaMensal > 0 ? taxaMensal : 0;
    // Taxa zero amortiza linearmente (nao existe Price sem juros).
    const pmt = i > 0 ? calcPMT(valor, parcelas, i) : valor / parcelas;
    let saldo = valor;
    let diario = 0;
    for (let k = 1; k <= parcelas; k++) {
        // Ultima parcela liquida o saldo: garante que a soma das amortizacoes
        // feche exatamente o principal, sem sobra de arredondamento.
        const amortizacao = k === parcelas ? saldo : pmt - saldo * i;
        saldo -= amortizacao;
        diario += amortizacao * IOF_DIARIO * Math.min(30 * k, IOF_DIAS_MAX);
    }
    return valor * IOF_ADICIONAL + diario;
}
export function calcPMT(valor, parcelas, taxaMensal) {
    // Taxa zero amortiza linearmente — nao existe Price sem juros, e a formula
    // de Price puro daria 0/0 (NaN) neste caso.
    if (taxaMensal === 0)
        return valor / parcelas;
    return (valor * taxaMensal) / (1 - Math.pow(1 + taxaMensal, -parcelas));
}
//# sourceMappingURL=cet.js.map